import React from 'react';
import { Bold, Italic, Code, Link, List, ListOrdered, Quote, Minus, Heading1, Heading2, Heading3, Image, CheckSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

const tools = [
  { icon: Heading1, label: 'Heading 1', prefix: '# ', type: 'prefix' },
  { icon: Heading2, label: 'Heading 2', prefix: '## ', type: 'prefix' },
  { icon: Heading3, label: 'Heading 3', prefix: '### ', type: 'prefix' },
  { type: 'separator' },
  { icon: Bold, label: 'Bold', prefix: '**', suffix: '**', shortcut: '⌘B', type: 'wrap' },
  { icon: Italic, label: 'Italic', prefix: '*', suffix: '*', shortcut: '⌘I', type: 'wrap' },
  { icon: Code, label: 'Code', prefix: '`', suffix: '`', shortcut: '⌘E', type: 'wrap' },
  { type: 'separator' },
  { icon: Link, label: 'Link', prefix: '[', suffix: '](url)', shortcut: '⌘K', type: 'wrap' },
  { icon: Image, label: 'Image', prefix: '![alt](', suffix: ')', type: 'wrap' },
  { type: 'separator' },
  { icon: List, label: 'Bullet List', prefix: '- ', type: 'prefix' },
  { icon: ListOrdered, label: 'Numbered List', prefix: '1. ', type: 'prefix' },
  { icon: CheckSquare, label: 'Task List', prefix: '- [ ] ', type: 'prefix' },
  { icon: Quote, label: 'Blockquote', prefix: '> ', type: 'prefix' },
  { icon: Minus, label: 'Divider', prefix: '\n---\n', type: 'insert' },
];

export default function MarkdownToolbar({ textareaRef, content, onChange }) {
  const insert = (tool) => {
    const ta = textareaRef?.current;
    if (!ta) return;
    const { selectionStart: s, selectionEnd: e, value: v } = ta;
    const selected = v.substring(s, e);

    if (tool.type === 'prefix') {
      const lineStart = v.lastIndexOf('\n', s - 1) + 1;
      const newText = v.substring(0, lineStart) + tool.prefix + (selected || '') + v.substring(e);
      onChange(newText);
      setTimeout(() => { ta.selectionStart = ta.selectionEnd = lineStart + tool.prefix.length + (selected?.length || 0); ta.focus(); }, 0);
    } else if (tool.type === 'wrap') {
      const text = selected || 'text';
      const newText = v.substring(0, s) + tool.prefix + text + (tool.suffix || '') + v.substring(e);
      onChange(newText);
      setTimeout(() => { ta.selectionStart = s + tool.prefix.length; ta.selectionEnd = s + tool.prefix.length + text.length; ta.focus(); }, 0);
    } else if (tool.type === 'insert') {
      const newText = v.substring(0, s) + tool.prefix + v.substring(e);
      onChange(newText);
      setTimeout(() => { ta.selectionStart = ta.selectionEnd = s + tool.prefix.length; ta.focus(); }, 0);
    }
  };

  return (
    <TooltipProvider>
      <div className="flex items-center gap-0.5 px-2 py-1 border-b bg-card flex-wrap">
        {tools.map((tool, i) =>
          tool.type === 'separator' ? (
            <Separator key={i} orientation="vertical" className="h-5 mx-1" />
          ) : (
            <Tooltip key={i}>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => insert(tool)}>
                  <tool.icon size={14} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{tool.label} {tool.shortcut && <kbd className="ml-1 text-xs bg-muted px-1 rounded">{tool.shortcut}</kbd>}</p>
              </TooltipContent>
            </Tooltip>
          )
        )}
      </div>
    </TooltipProvider>
  );
}
